import { useEffect, useState } from 'react';

import { getBlockStatus } from '../../util/api';
import { blockedUserIdsOf } from '../../ducks/user.duck';

/**
 * Can the signed-in user and `otherUserId` contact each other? (P25028-272)
 *
 * `blockedByMe` is read from the signed-in user's own private data, so it changes the
 * moment they block or unblock. Whether the other user blocked them can only be read on
 * the server, through /api/block-status, which is asked again after every such change.
 *
 * Returns { isBlocked, blockedByMe, canBlock }. `canBlock` is false when nobody is signed
 * in, the other user is not known yet, or it is the user's own page (AC5). If the server
 * cannot answer, the pair is treated as not blocked: orders are still checked on the
 * server, and a page that hides Buy Now by mistake would be worse.
 */
const useBlockStatus = ({ currentUser, otherUserId }) => {
  const myId = currentUser?.id?.uuid;
  const blockedByMe = !!otherUserId && blockedUserIdsOf(currentUser).includes(otherUserId);
  const canBlock = !!myId && !!otherUserId && myId !== otherUserId;

  // The server's answer is kept with the question it answers, so an answer that arrives
  // after the user changed their own list is never shown for the new state.
  const requestKey = canBlock ? `${myId}:${otherUserId}:${blockedByMe}` : null;
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    if (!requestKey) {
      return undefined;
    }
    let cancelled = false;
    getBlockStatus(otherUserId)
      .then(response => {
        if (!cancelled) {
          setAnswer({ requestKey, isBlocked: !!response?.isBlocked });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAnswer({ requestKey, isBlocked: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestKey]);

  const blockedByServer = answer?.requestKey === requestKey ? answer.isBlocked : false;

  return { isBlocked: canBlock && (blockedByMe || blockedByServer), blockedByMe, canBlock };
};

export default useBlockStatus;
